/**
 * Device Detection and Capability Module
 * Centralizes all device/browser/platform detection logic
 */
import { ClassLogger } from './logging/class_logger'

const logger = new ClassLogger('DeviceDetection')
const log = logger.log.bind(logger)

export class DeviceDetection {
  static getDeviceOverride () {
    const params = new URLSearchParams(window.location.search)
    return params.get('device_test')
  }

  static getUserAgent () {
    return window.navigator.userAgent
  }

  static isIOS () {
    const override = this.getDeviceOverride()
    if (override === 'ios' || override === 'ios_pwa') {
      log('Simulating iOS')
      return true
    }
    if (override === 'android' || override === 'android_pwa') {
      log(`Simulating ${override}, not iOS`)
      return false
    }

    const userAgent = this.getUserAgent().toLowerCase()
    const basicMatch = /iphone|ipad|ipod/.test(userAgent)
    if (basicMatch) return true

    const isPadLikeSafari = userAgent.includes('safari') &&
                            userAgent.includes('macintosh') &&
                            navigator.maxTouchPoints > 1
    return isPadLikeSafari
  }

  static isAndroid () {
    const override = this.getDeviceOverride()
    if (override === 'android' || override === 'android_pwa') {
      log('Simulating Android')
      return true
    }
    if (override === 'ios' || override === 'ios_pwa') {
      log(`Simulating ${override}, not Android`)
      return false
    }

    return this.getUserAgent().includes('Android')
  }

  static isPWA () {
    const override = this.getDeviceOverride()
    if (override === 'ios_pwa' || override === 'android_pwa') {
      log('Simulating PWA')
      return true
    }
    if (override === 'ios' || override === 'android') {
      log(`Simulating ${override}, not PWA`)
      return false
    }

    return window.matchMedia('(display-mode: standalone)').matches ||
           window.navigator.standalone === true
  }

  static isIOSPWA () {
    const override = this.getDeviceOverride()
    if (override === 'ios_pwa') {
      log('Simulating iOS PWA')
      return true
    }
    if (override === 'ios' || override === 'android' || override === 'android_pwa') {
      log(`Simulating ${override}, not iOS PWA`)
      return false
    }

    return this.isIOS() && this.isPWA()
  }

  static isAndroidPWA () {
    const override = this.getDeviceOverride()
    if (override === 'android_pwa') {
      log('Simulating Android PWA')
      return true
    }
    if (override === 'ios' || override === 'ios_pwa' || override === 'android') {
      log(`Simulating ${override}, not Android PWA`)
      return false
    }

    return this.isAndroid() && this.isPWA()
  }

  static isMobileDevice () {
    const override = this.getDeviceOverride()
    if (override === 'android' || override === 'android_pwa' ||
        override === 'ios' || override === 'ios_pwa') {
      return true
    }

    const userAgent = this.getUserAgent().toLowerCase()
    const basicMatch = /iphone|ipad|ipod|android/.test(userAgent)
    if (basicMatch) return true

    const isPadLikeSafari = userAgent.includes('safari') &&
                            userAgent.includes('macintosh') &&
                            navigator.maxTouchPoints > 1
    return isPadLikeSafari
  }

  static getIOSVersion () {
    if (!this.isIOS()) return null

    const ua = this.getUserAgent()

    let match = ua.match(/OS (\d+)_(\d+)(_(\d+))?/)
    if (match) {
      const major = parseInt(match[1], 10)
      const minor = parseInt(match[2], 10)
      const patch = parseInt(match[4] || '0', 10)
      return {
        major,
        minor,
        patch,
        string: `${major}.${minor}.${patch}`
      }
    }

    match = ua.match(/Version\/(\d+)\.(\d+)(\.(\d+))?/)
    if (match) {
      const major = parseInt(match[1], 10)
      const minor = parseInt(match[2], 10)
      const patch = parseInt(match[4] || '0', 10)
      return {
        major,
        minor,
        patch,
        string: `${major}.${minor}.${patch}`
      }
    }

    return null
  }

  static isChromeDesktop () {
    const ua = this.getUserAgent()
    return navigator.vendor.includes('Google') &&
           ua.includes('Chrome') &&
           !ua.includes('Mobile')
  }

  static isFirefoxDesktop () {
    const ua = this.getUserAgent()
    return ua.includes('Firefox') && !ua.includes('Mobile')
  }

  static isSafariDesktop () {
    const ua = this.getUserAgent()
    return navigator.vendor.includes('Apple') &&
           ua.includes('Safari') &&
           !ua.includes('Mobile')
  }

  static isSafari () {
    return /^((?!chrome|android).)*safari/i.test(this.getUserAgent())
  }

  static isMobile () {
    return /Mobi/i.test(this.getUserAgent())
  }

  static isMaybeIOSSimulator () {
    const ua = this.getUserAgent()
    const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream
    const noTouchID = !('webkitTouchID' in window)
    const noFaceID = !('webkitFaceID' in window)
    return isIOS && noTouchID && noFaceID
  }

  static isSecureContext () {
    return window.location.protocol === 'https:' ||
           window.location.hostname === 'localhost'
  }

  static hasServiceWorkerSupport () {
    return navigator.serviceWorker !== undefined
  }

  static hasNotificationSupport () {
    return window.Notification !== undefined
  }

  static hasPushManagerInWindow () {
    return 'pushManager' in window
  }
}

export class NotificationAvailability {
  static canUseFirebase () {
    if (!DeviceDetection.isSecureContext()) {
      return { available: false, reason: 'Requires HTTPS connection' }
    }
    if (!DeviceDetection.hasServiceWorkerSupport()) {
      return { available: false, reason: 'Service Workers not supported' }
    }
    if (!DeviceDetection.hasNotificationSupport()) {
      return { available: false, reason: 'Notifications not supported' }
    }
    if (DeviceDetection.isIOSPWA()) {
      return { available: false, reason: 'Use Mobile Notifications in iOS PWA' }
    }
    return { available: true, reason: null }
  }

  static canUseDeclarativeWebPush (pwaInstallUrl) {
    if (!DeviceDetection.isPWA()) {
      const isMobile = DeviceDetection.isMobileDevice()
      log('canUseDeclarativeWebPush: isMobile =', isMobile)
      log('canUseDeclarativeWebPush: userAgent =', DeviceDetection.getUserAgent())
      const reason = isMobile
        ? {
            html: true,
            message: `To manage these, please open <a href="${pwaInstallUrl}" class="underline">the app</a>`
          }
        : {
            html: false,
            message: 'To manage these, please open the app on your phone or tablet'
          }
      return { available: false, reason }
    }
    if (!DeviceDetection.isIOS()) {
      return {
        available: false,
        reason: { html: false, message: 'Declarative Web Push requires iOS' }
      }
    }
    if (!DeviceDetection.hasNotificationSupport()) {
      return {
        available: false,
        reason: { html: false, message: 'Notifications not supported' }
      }
    }
    return { available: true, reason: null }
  }
}
